import { useEffect, useState, useRef } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  MapPin,
  BedDouble,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Maximize2,
  Eye,
  CheckCircle2,
  Building2,
  Check
} from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { WhatsAppFloat } from "@/components/site/WhatsAppFloat";
import { QuoteDialog } from "@/components/site/QuoteDialog";
import { FinalCTA } from "@/components/site/FinalCTA";
import { Reveal } from "@/components/site/Reveal";
import { CountUp } from "@/components/site/CountUp";
import { BeforeAfterSlider } from "@/components/site/BeforeAfterSlider";
import { ProjectDetailModal } from "@/components/site/ProjectDetailModal";
import { PROJECTS_GROUPS } from "@/lib/projectsData";

const HERO_IMG =
  "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=2400&q=85";

// Flatten all items into a flat list for filtering & filmstrip
const ALL_PROJECTS = PROJECTS_GROUPS.flatMap((group) =>
  group.areas.flatMap((area) =>
    area.items.map((item) => ({
      ...item,
      city: group.city,
      area: area.area,
      coords: area.coords,
    }))
  )
);

// High impact before & after images for Dubai properties
const BEFORE_IMG = "https://images.unsplash.com/photo-1513694203232-719a280e022f?auto=format&fit=crop&w=1600&q=85";
const AFTER_IMG = "https://images.unsplash.com/photo-1616594039964-ae9021a400a0?auto=format&fit=crop&w=1600&q=85";

export default function ProjectShowcase() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeCity, setActiveCity] = useState("All");
  const [activeFilter, setActiveFilter] = useState("All");
  const [selectedAreaIndex, setSelectedAreaIndex] = useState(0);
  const [spotlightIndex, setSpotlightIndex] = useState(0);

  const reelRef = useRef(null);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 100]);

  useEffect(() => {
    document.title =
      "Interactive Showcase Studio | Luxury Projects — Bloc & Brush Dubai";
    window.scrollTo(0, 0);
  }, []);

  // Filter projects by city & BHK category
  const filteredProjects = ALL_PROJECTS.filter((p) => {
    const matchesCity = activeCity === "All" || p.city === activeCity;
    const matchesFilter =
      activeFilter === "All" ||
      (activeFilter === "Penthouse" && p.bhk.includes("Penthouse")) ||
      (activeFilter === "1 BHK" && p.bhk.includes("1 BHK")) ||
      (activeFilter === "2 BHK" && p.bhk.includes("2 BHK")) ||
      (activeFilter === "Large" && (p.bhk.includes("3 BHK") || p.bhk.includes("4 BHK") || p.bhk.includes("5 BHK")));
    return matchesCity && matchesFilter;
  });

  const featuredSpotlights = ALL_PROJECTS.slice(0, 4);

  const scrollReel = (direction) => {
    if (reelRef.current) {
      const scrollAmount = direction === "left" ? -400 : 400;
      reelRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  // Grouped areas for active city view
  const currentCityData =
    activeCity === "All"
      ? PROJECTS_GROUPS[0]
      : PROJECTS_GROUPS.find((g) => g.city === activeCity) || PROJECTS_GROUPS[0];

  const currentArea = currentCityData.areas[selectedAreaIndex] || currentCityData.areas[0];

  return (
    <div className="bg-[#FFFFFF] text-[#1C1C1C] min-h-screen">
      <Navbar onQuoteClick={() => setQuoteOpen(true)} />

      {/* STAGE 0: HERO SECTION - FIT EXACTLY TO (100vh - 80px sticky navbar height) */}
      <section
        data-testid="showcase-hero"
        className="relative pt-20 min-h-[calc(100vh-5rem)] lg:h-[calc(100vh-5rem)] flex flex-col justify-center overflow-hidden bg-[#0E1F3A]"
      >
        <motion.div style={{ y: heroY }} className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Bloc & Brush Project Showcase"
            className="w-full h-full object-cover opacity-40"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0E1F3A] via-[#0E1F3A]/60 to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(19,41,75,0.4),transparent_70%)]" />

        <div className="relative container mx-auto px-6 lg:px-12 max-w-7xl py-8 text-white z-10 flex flex-col justify-between h-full">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-xl px-4 py-1.5 rounded-full border border-white/15 text-xs uppercase tracking-[0.2em] text-white/90 mb-4"
            >
              <Building2 size={14} className="text-[#25D366]" /> Exclusive Architectural Experience
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display text-4xl md:text-5xl lg:text-[64px] font-extrabold leading-[1.02] tracking-tight max-w-4xl"
            >
              Spatial Excellence. <br />
              <span className="text-white/65">Interactive Showcase.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-4 text-sm md:text-base text-white/80 max-w-2xl leading-relaxed font-normal"
            >
              Explore our turnkey luxury fit-outs and holiday home transformations through an interactive visual studio stage. Compare before & after work, filter by district, or inspect project specs directly.
            </motion.p>
          </div>

          <div>
            {/* FILTER CONTROLS RIBBON */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="pt-6 border-t border-white/15 flex flex-wrap items-center justify-between gap-4"
            >
              {/* CITY SWITCHER */}
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-[0.2em] text-white/50 mr-2 font-semibold">City:</span>
                {["All", "Dubai", "Sharjah"].map((city) => (
                  <button
                    key={city}
                    onClick={() => {
                      setActiveCity(city);
                      setSelectedAreaIndex(0);
                    }}
                    className={`px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-all duration-300 ${
                      activeCity === city
                        ? "bg-white text-[#13294B] shadow-lg"
                        : "bg-white/10 text-white/80 hover:bg-white/20 border border-white/10"
                    }`}
                  >
                    {city}
                  </button>
                ))}
              </div>

              {/* BHK TYPE FILTER */}
              <div className="flex flex-wrap items-center gap-2">
                <SlidersHorizontal size={14} className="text-white/50 mr-1" />
                {["All", "1 BHK", "2 BHK", "Large", "Penthouse"].map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setActiveFilter(filter)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all ${
                      activeFilter === filter
                        ? "bg-[#25D366] text-white shadow-md font-semibold"
                        : "bg-black/30 text-white/70 hover:text-white border border-white/10"
                    }`}
                  >
                    {filter}
                  </button>
                ))}
              </div>
            </motion.div>

            {/* STATS STRIP */}
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 border-t border-white/10">
              <div>
                <div className="font-display text-2xl md:text-3xl font-extrabold">
                  <CountUp end={ALL_PROJECTS.length} suffix="+" />
                </div>
                <div className="mt-0.5 text-[10px] uppercase tracking-[0.18em] text-white/60">Delivered Residences</div>
              </div>
              <div>
                <div className="font-display text-2xl md:text-3xl font-extrabold">14-21</div>
                <div className="mt-0.5 text-[10px] uppercase tracking-[0.18em] text-white/60">Days Turnkey Speed</div>
              </div>
              <div>
                <div className="font-display text-2xl md:text-3xl font-extrabold">100%</div>
                <div className="mt-0.5 text-[10px] uppercase tracking-[0.18em] text-white/60">Server Hosted</div>
              </div>
              <div>
                <div className="font-display text-2xl md:text-3xl font-extrabold">5.0 ★</div>
                <div className="mt-0.5 text-[10px] uppercase tracking-[0.18em] text-white/60">Client Rating</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STAGE 1: FEATURED SPOTLIGHT CAROUSEL - FIT EXACTLY TO (100vh - 80px) & FIXED CARD HEIGHT */}
      <section className="min-h-[calc(100vh-5rem)] lg:h-[calc(100vh-5rem)] flex flex-col justify-center py-6 lg:py-8 bg-[#F5F5F5] overflow-hidden border-b border-black/5">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl flex flex-col justify-between h-full">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-4">
              <div>
                <span className="text-xs uppercase tracking-[0.25em] text-[#707070] font-semibold">
                  Featured Showcase Stage
                </span>
                <h2 className="font-display mt-1 text-2xl md:text-3xl lg:text-[40px] font-extrabold text-[#1C1C1C] leading-tight">
                  Iconic Living Spaces.
                </h2>
              </div>
              <p className="text-xs md:text-sm text-[#707070] max-w-md">
                Select any spotlight project below to view full architectural scope, bespoke joinery, and full image gallery.
              </p>
            </div>
          </Reveal>

          {/* SPLIT SPOTLIGHT STAGE WITH FIXED CONTAINER HEIGHT (NO LAYOUT JUMPING) */}
          <div className="bg-white rounded-3xl border border-black/5 shadow-[0_15px_40px_rgba(19,41,75,0.06)] overflow-hidden grid grid-cols-1 lg:grid-cols-12 h-[480px] lg:h-[500px]">
            {/* LEFT IMAGE STAGE (7 COLS) - FIXED HEIGHT */}
            <div className="lg:col-span-7 relative h-full overflow-hidden bg-gray-900 group">
              <AnimatePresence mode="wait">
                <motion.img
                  key={featuredSpotlights[spotlightIndex].image}
                  src={featuredSpotlights[spotlightIndex].image}
                  alt={featuredSpotlights[spotlightIndex].title}
                  initial={{ opacity: 0, scale: 1.04 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.5 }}
                  className="w-full h-full object-cover"
                />
              </AnimatePresence>

              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-black/30 pointer-events-none" />

              <div className="absolute top-5 left-5 flex items-center gap-2 z-10">
                <span className="bg-[#13294B] text-white text-[11px] font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-md">
                  Spotlight 0{spotlightIndex + 1} / 0{featuredSpotlights.length}
                </span>
                <span className="bg-white/90 text-[#13294B] text-[11px] font-semibold px-3 py-1 rounded-full backdrop-blur">
                  {featuredSpotlights[spotlightIndex].bhk}
                </span>
              </div>

              <Link
                to={`/projects/${featuredSpotlights[spotlightIndex].slug || featuredSpotlights[spotlightIndex].id}`}
                className="absolute bottom-5 right-5 bg-white/95 text-[#13294B] hover:bg-white px-4 py-2.5 rounded-full text-xs font-bold tracking-wider uppercase flex items-center gap-2 shadow-xl backdrop-blur transition-transform hover:scale-105 z-10"
              >
                <Maximize2 size={14} /> View Full Project Page
              </Link>
            </div>

            {/* RIGHT DETAILS STAGE (5 COLS) - FIXED HEIGHT FLEX LAYOUT */}
            <div className="lg:col-span-5 p-6 lg:p-8 flex flex-col justify-between bg-white h-full overflow-y-auto">
              <div>
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#707070] font-semibold mb-2">
                  <MapPin size={13} className="text-[#13294B]" />
                  {featuredSpotlights[spotlightIndex].area} · {featuredSpotlights[spotlightIndex].city}
                </div>

                <h3 className="font-display text-xl lg:text-2xl font-extrabold text-[#1C1C1C] leading-snug">
                  {featuredSpotlights[spotlightIndex].title}
                </h3>

                <p className="mt-3 text-xs md:text-sm text-[#1C1C1C]/75 leading-relaxed line-clamp-3">
                  {featuredSpotlights[spotlightIndex].description}
                </p>

                <div className="mt-4 space-y-2 pt-4 border-t border-black/5">
                  <div className="flex items-center gap-2.5 text-xs text-[#1C1C1C]/80">
                    <CheckCircle2 size={15} className="text-[#25D366] shrink-0" />
                    <span>Turnkey Civil Works & Custom Joinery</span>
                  </div>
                  <div className="flex items-center gap-2.5 text-xs text-[#1C1C1C]/80">
                    <CheckCircle2 size={15} className="text-[#25D366] shrink-0" />
                    <span>Complete Guest Linen & Kitchen Pack</span>
                  </div>
                  <div className="flex items-center gap-2.5 text-xs text-[#1C1C1C]/80">
                    <CheckCircle2 size={15} className="text-[#25D366] shrink-0" />
                    <span>Dubai Municipality & DTCM Approvals</span>
                  </div>
                </div>
              </div>

              {/* CONTROLS & NAV */}
              <div className="mt-6 pt-4 border-t border-black/5 flex items-center justify-between gap-4">
                <div className="flex items-center gap-1.5">
                  {featuredSpotlights.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setSpotlightIndex(i)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        spotlightIndex === i ? "w-6 bg-[#13294B]" : "w-2 bg-black/15 hover:bg-black/30"
                      }`}
                      aria-label={`Go to slide ${i + 1}`}
                    />
                  ))}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSpotlightIndex((prev) => (prev === 0 ? featuredSpotlights.length - 1 : prev - 1))}
                    className="w-9 h-9 rounded-full border border-black/10 flex items-center justify-center text-[#13294B] hover:bg-[#13294B] hover:text-white transition"
                    aria-label="Previous spotlight"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    onClick={() => setSpotlightIndex((prev) => (prev === featuredSpotlights.length - 1 ? 0 : prev + 1))}
                    className="w-9 h-9 rounded-full border border-black/10 flex items-center justify-center text-[#13294B] hover:bg-[#13294B] hover:text-white transition"
                    aria-label="Next spotlight"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STAGE 2: INTERACTIVE DISTRICT & MASTERPLAN EXPLORER - FIT EXACTLY TO (100vh - 80px) */}
      <section className="min-h-[calc(100vh-5rem)] lg:h-[calc(100vh-5rem)] flex flex-col justify-center py-6 lg:py-8 bg-white border-b border-black/5">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl flex flex-col justify-between h-full">
          {/* SECTION HEADER & COMMUNITY SELECTION TABS */}
          <div className="space-y-4 mb-4 shrink-0">
            <Reveal>
              <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
                <div>
                  <span className="text-xs uppercase tracking-[0.25em] text-[#707070] font-semibold">
                    District & Community Stage
                  </span>
                  <h2 className="font-display mt-1 text-2xl md:text-3xl lg:text-[38px] font-extrabold text-[#1C1C1C] leading-tight">
                    Explore By Neighborhood.
                  </h2>
                </div>
                <div className="text-xs text-[#707070] shrink-0">
                  Select a community to reveal turnkey project specs and server portfolio.
                </div>
              </div>
            </Reveal>

            {/* COMMUNITY SELECTION TABS (ISOLATED FLEX ROW WITH NO OVERLAP) */}
            <div className="flex items-center gap-2 overflow-x-auto pt-1 pb-1 hide-scrollbar">
              {currentCityData.areas.map((area, idx) => (
                <button
                  key={area.area}
                  onClick={() => setSelectedAreaIndex(idx)}
                  className={`px-4 py-2 rounded-xl text-xs font-bold tracking-wide whitespace-nowrap transition-all duration-300 flex items-center gap-2 border shrink-0 ${
                    selectedAreaIndex === idx
                      ? "bg-[#13294B] text-white border-[#13294B] shadow-md scale-102"
                      : "bg-[#F5F5F5] text-[#1C1C1C]/75 border-black/5 hover:bg-black/5"
                  }`}
                >
                  <MapPin size={13} className="shrink-0" />
                  <span>{area.area}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${selectedAreaIndex === idx ? "bg-white/20 text-white" : "bg-black/5 text-[#707070]"}`}>
                    {area.items.length}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* ACTIVE COMMUNITY ASYMMETRIC STAGE */}
          {currentArea && (
            <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch h-[440px] lg:h-[460px]">
              {/* PRIMARY ASYMMETRIC HERO CARD (7 COLS) */}
              <div className="lg:col-span-7 bg-[#F5F5F5] rounded-3xl p-6 lg:p-8 border border-black/5 relative overflow-hidden flex flex-col justify-between h-full">
                <div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-[11px] uppercase tracking-[0.25em] font-semibold text-[#707070]">
                      Community Hub · {currentCityData.city}
                    </span>
                    <span className="bg-[#13294B] text-white text-[11px] font-semibold px-3 py-0.5 rounded-full">
                      {currentArea.coords}
                    </span>
                  </div>

                  <h3 className="font-display mt-2 text-2xl font-extrabold text-[#1C1C1C]">
                    {currentArea.area} Portfolio
                  </h3>

                  <div className="mt-4 aspect-[16/9] rounded-2xl overflow-hidden relative shadow-md group h-[220px]">
                    <img
                      src={currentArea.items[0]?.image}
                      alt={currentArea.items[0]?.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4 text-white flex items-center justify-between">
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-[#25D366]">
                          Featured Property
                        </div>
                        <h4 className="font-display text-base font-bold">{currentArea.items[0]?.title}</h4>
                      </div>
                      <Link
                        to={`/projects/${currentArea.items[0]?.slug || currentArea.items[0]?.id}`}
                        className="bg-white text-[#13294B] p-2 rounded-full hover:scale-110 transition"
                      >
                        <Eye size={16} />
                      </Link>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-black/10 flex items-center justify-between text-xs text-[#707070]">
                  <span>Average Turnkey Speed: 16 Days</span>
                  <button
                    onClick={() => setQuoteOpen(true)}
                    className="text-[#13294B] font-bold hover:underline flex items-center gap-1"
                  >
                    Furnish Your Property Here <ArrowRight size={13} />
                  </button>
                </div>
              </div>

              {/* SECONDARY LIST STACK (5 COLS) - SCROLLABLE STACK */}
              <div className="lg:col-span-5 space-y-3 h-full overflow-y-auto pr-1 custom-scrollbar">
                {currentArea.items.map((item) => (
                  <motion.div
                    key={item.title}
                    whileHover={{ x: 4 }}
                    className="bg-white rounded-2xl p-4 border border-black/5 hover:border-[#13294B]/30 hover:shadow-md transition-all duration-300 flex items-center justify-between gap-3 cursor-pointer"
                    onClick={() => setSelectedProject(item)}
                  >
                    <div className="flex items-center gap-3">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-16 h-16 rounded-xl object-cover shrink-0"
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-[#13294B] bg-[#F5F5F5] px-2 py-0.5 rounded">
                            {item.bhk}
                          </span>
                          <span className="text-[10px] text-[#707070]">{currentArea.area}</span>
                        </div>
                        <h4 className="font-display text-sm font-bold text-[#1C1C1C] mt-1 line-clamp-1">
                          {item.title}
                        </h4>
                        <p className="text-[11px] text-[#707070] mt-0.5 line-clamp-1">
                          {item.description}
                        </p>
                      </div>
                    </div>

                    <Link
                      to={`/projects/${item.slug || item.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="w-8 h-8 rounded-full bg-[#F5F5F5] text-[#13294B] flex items-center justify-center shrink-0 hover:bg-[#13294B] hover:text-white transition"
                      aria-label="Inspect project"
                    >
                      <ArrowUpRight size={15} />
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* STAGE 3: INTERACTIVE BEFORE & AFTER TRANSFORMATION - FIT EXACTLY TO (100vh - 80px) */}
      <section className="min-h-[calc(100vh-5rem)] lg:h-[calc(100vh-5rem)] flex flex-col justify-center py-6 lg:py-8 bg-[#0E1F3A] text-white overflow-hidden">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl flex flex-col justify-between h-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center h-full">
            {/* LEFT TEXT (5 COLS) */}
            <div className="lg:col-span-5">
              <span className="text-xs uppercase tracking-[0.25em] text-[#25D366] font-semibold">
                Transformation Studio
              </span>
              <h2 className="font-display mt-2 text-3xl lg:text-[42px] font-extrabold leading-tight">
                From Bare Shell To 5-Star Luxury.
              </h2>
              <p className="mt-4 text-xs md:text-sm text-white/75 leading-relaxed">
                Drag the interactive slider on the right to inspect how Bloc & Brush takes empty developer units and transforms them into turnkey luxury apartments in under 3 weeks.
              </p>

              <div className="mt-6 space-y-3">
                <div className="flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-[#25D366]/20 text-[#25D366] flex items-center justify-center shrink-0 mt-0.5">
                    <Check size={12} />
                  </div>
                  <div>
                    <h4 className="font-display text-xs font-bold">Civil Prep & Custom Wall Finishes</h4>
                    <p className="text-[11px] text-white/60">Dustless painting, decorative paneling & lighting.</p>
                  </div>
                </div>

                <div className="flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-[#25D366]/20 text-[#25D366] flex items-center justify-center shrink-0 mt-0.5">
                    <Check size={12} />
                  </div>
                  <div>
                    <h4 className="font-display text-xs font-bold">Full Inventory & Kitchen Setup</h4>
                    <p className="text-[11px] text-white/60">Appliances, cutlery, bedding and guest essentials.</p>
                  </div>
                </div>

                <div className="flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-[#25D366]/20 text-[#25D366] flex items-center justify-center shrink-0 mt-0.5">
                    <Check size={12} />
                  </div>
                  <div>
                    <h4 className="font-display text-xs font-bold">DTCM & Permit Approvals</h4>
                    <p className="text-[11px] text-white/60">Dubai Municipality permit coordination start to finish.</p>
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <button
                  onClick={() => setQuoteOpen(true)}
                  className="bg-[#25D366] hover:bg-[#20bd5a] text-white px-6 py-3 rounded-full font-bold text-xs tracking-wide transition-all shadow-lg hover:scale-105"
                >
                  Transform Your Property Now
                </button>
              </div>
            </div>

            {/* RIGHT SLIDER (7 COLS) - FIXED HEIGHT ASPECT RATIO */}
            <div className="lg:col-span-7 h-[380px] lg:h-[420px] flex items-center">
              <BeforeAfterSlider
                beforeImage={BEFORE_IMG}
                afterImage={AFTER_IMG}
                beforeLabel="Before: Empty Unit"
                afterLabel="After: Bloc & Brush Luxury Finish"
                className="w-full h-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* STAGE 4: HORIZONTAL FILMSTRIP REEL - FIT EXACTLY TO (100vh - 80px) */}
      <section className="min-h-[calc(100vh-5rem)] lg:h-[calc(100vh-5rem)] flex flex-col justify-center py-6 lg:py-8 bg-[#F5F5F5] overflow-hidden border-b border-black/5">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl flex flex-col justify-between h-full">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
              <div>
                <span className="text-xs uppercase tracking-[0.25em] text-[#707070] font-semibold">
                  Curated Showcase Track
                </span>
                <h2 className="font-display mt-1 text-2xl md:text-3xl lg:text-[40px] font-extrabold text-[#1C1C1C]">
                  Horizontal Property Reel.
                </h2>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => scrollReel("left")}
                  className="w-10 h-10 rounded-full bg-white border border-black/10 flex items-center justify-center text-[#13294B] shadow-sm hover:bg-[#13294B] hover:text-white transition"
                  aria-label="Scroll left"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  onClick={() => scrollReel("right")}
                  className="w-10 h-10 rounded-full bg-white border border-black/10 flex items-center justify-center text-[#13294B] shadow-sm hover:bg-[#13294B] hover:text-white transition"
                  aria-label="Scroll right"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </Reveal>

          {/* DRAGGABLE REEL TRACK */}
          <div
            ref={reelRef}
            className="flex gap-6 overflow-x-auto snap-x snap-mandatory hide-scrollbar pb-6 pt-1"
          >
            {filteredProjects.map((p, idx) => (
              <motion.div
                key={p.title + idx}
                whileHover={{ y: -6 }}
                className="snap-start shrink-0 w-[300px] sm:w-[350px] bg-white rounded-3xl overflow-hidden border border-black/5 shadow-[0_10px_25px_rgba(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgba(19,41,75,0.12)] transition-all duration-300 cursor-pointer flex flex-col justify-between h-[390px]"
                onClick={() => setSelectedProject(p)}
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-gray-900 h-[210px]">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute top-3 left-3 bg-white/95 backdrop-blur px-2.5 py-0.5 rounded-full text-[11px] font-bold text-[#13294B]">
                    {p.bhk}
                  </div>
                  <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur text-white text-[11px] px-2.5 py-0.5 rounded-full font-medium flex items-center gap-1">
                    <MapPin size={11} /> {p.area}
                  </div>
                </div>

                <div className="p-5 flex-1 flex flex-col justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#707070]">
                      {p.city} · {p.coords}
                    </div>
                    <h4 className="font-display mt-1 text-base font-bold text-[#1C1C1C] line-clamp-1">
                      {p.title}
                    </h4>
                    <p className="mt-1.5 text-xs text-[#707070] line-clamp-2 leading-relaxed">
                      {p.description}
                    </p>
                  </div>

                  <div className="mt-4 pt-3 border-t border-black/5 flex items-center justify-between">
                    <span className="text-xs font-bold text-[#707070] flex items-center gap-1">
                      Quick Inspect <Eye size={13} />
                    </span>
                    <Link
                      to={`/projects/${p.slug || p.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-xs text-[#13294B] font-bold hover:underline flex items-center gap-1"
                    >
                      Full Project <ArrowUpRight size={13} />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl flex flex-col md:flex-row md:items-center justify-between gap-6 bg-[#F5F5F5] p-8 lg:p-12 rounded-3xl border border-black/5">
          <div className="max-w-2xl">
            <h2 className="font-display text-2xl lg:text-3xl font-extrabold tracking-tight text-[#1C1C1C]">
              Ready to feature your property in our showcase?
            </h2>
            <p className="mt-2 text-sm text-[#707070] leading-relaxed">
              We provide turnkey interior fit-outs, holiday home furnishings, and authority approvals across Dubai & Sharjah.
            </p>
          </div>
          <button
            onClick={() => setQuoteOpen(true)}
            data-testid="showcase-cta-btn"
            className="inline-flex items-center gap-2 bg-[#13294B] hover:bg-[#0E1F3A] text-white rounded-full px-7 py-3.5 text-sm font-medium transition-all shadow-md shrink-0"
          >
            Get Free Project Estimate <ArrowRight size={15} />
          </button>
        </div>
      </section>

      {/* LIGHTBOX MODAL */}
      <ProjectDetailModal
        project={selectedProject}
        area={selectedProject?.area}
        city={selectedProject?.city}
        isOpen={!!selectedProject}
        onClose={() => setSelectedProject(null)}
        onQuoteClick={() => setQuoteOpen(true)}
      />

      <FinalCTA onQuoteClick={() => setQuoteOpen(true)} />
      <Footer onQuoteClick={() => setQuoteOpen(true)} />
      <WhatsAppFloat />
      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
    </div>
  );
}
